import React from 'react';
import isString from 'lodash/isString';
import { useStyletron } from 'baseui';
import { BooleanColumn, NumericalColumn, CustomColumn, CategoricalColumn } from 'baseui/data-table';
import { APP_LANGUAGE } from 'commons/models/common';
import { EMPTY_STRING } from 'commons-ui';
import { isNil } from 'lodash';

export const COL_NAMES = {
	name: 'name',
	type: 'type',
	launchWidgetType: 'launchWidgetType',
	version: 'version',
	enabled: 'enabled',
	isArchived: 'isArchived',
	titleText: 'titleText',
	titleColor: 'titleColor',
	subtitleText: 'subtitleText',
	subtitleColor: 'subtitleColor',
	cardBgColor: 'cardBgColor',
	cardBgUrl: 'cardBgUrl',
	isCrossable: 'isCrossable',
	cta: 'cta',
	imageUrl: 'imageUrl',
	videoUrl: 'videoUrl',
	minAppVersion: 'minAppVersion',
	maxAppVersion: 'maxAppVersion',
	validFrom: 'validFrom',
	validTill: 'validTill',
	expiry: 'expiry',
	repeat: 'repeat',
	lifetime: 'lifetime',
	priority: 'priority',
	inclusiveSegments: 'inclusiveSegments',
	exclusiveSegments: 'exclusiveSegments',
	abExperiments: 'abExperiments',
	minGames: 'minGames'
};

export const columnConfigs = [
	CategoricalColumn({
		title: 'Name (Internal Use)',
		maxWidth: 300,
		mapDataToValue: (data: any) => data[COL_NAMES.name]
	}),
	CategoricalColumn({
		title: 'Type',
		maxWidth: 300,
		mapDataToValue: (data: any) => data[COL_NAMES.type]
	}),
	CategoricalColumn({
		title: 'Launch Widget Type',
		maxWidth: 300,
		mapDataToValue: (data: any) => data[COL_NAMES.launchWidgetType] || 'NOTICE'
	}),
	CategoricalColumn({
		title: 'Version',
		maxWidth: 300,
		mapDataToValue: (data: any) => data[COL_NAMES.version] || EMPTY_STRING
	}),
	BooleanColumn({
		title: 'Enabled',
		mapDataToValue: (data: any) => data[COL_NAMES.enabled] || false,
		maxWidth: 200
	}),
	BooleanColumn({
		title: 'Archived',
		maxWidth: 300,
		mapDataToValue: (data: any) => data[COL_NAMES.isArchived] || false
	}),
	CategoricalColumn({
		title: 'CTA',
		maxWidth: 300,
		mapDataToValue: (data: any) => (data[COL_NAMES.cta] ? JSON.stringify(data[COL_NAMES.cta]) : EMPTY_STRING),
		type: 'string'
	}),
	CategoricalColumn({
		title: 'Valid From',
		maxWidth: 300,
		mapDataToValue: (data: any) => {
			if (!isNil(data[COL_NAMES.validFrom])) {
				return new Date(data[COL_NAMES.validFrom]).toString();
			}
			return 'Infinite';
		}
	}),
	CategoricalColumn({
		title: 'Valid Till',
		maxWidth: 220,
		mapDataToValue: (data: any) => {
			if (!isNil(data[COL_NAMES.validTill])) {
				return new Date(data[COL_NAMES.validTill]).toString();
			}
			return 'Infinite';
		}
	}),
	CategoricalColumn({
		title: 'Expiry Once Seen (Days)',
		maxWidth: 200,
		mapDataToValue: (data: any) => (data[COL_NAMES.expiry] === 0 ? 'Never' : data[COL_NAMES.expiry])
	}),
	CategoricalColumn({
		title: 'Repeat After (Days)',
		maxWidth: 200,
		mapDataToValue: (data: any) => (data[COL_NAMES.repeat] === 0 ? 'Every Session' : data[COL_NAMES.repeat])
	}),
	NumericalColumn({
		title: 'Lifetime',
		maxWidth: 200,
		mapDataToValue: (data: any) => data[COL_NAMES.lifetime] || 0
	}),
	NumericalColumn({
		title: 'Priority',
		maxWidth: 200,
		mapDataToValue: (data: any) => data[COL_NAMES.priority] || 0
	}),
	CustomColumn({
		title: 'Card Bg Color',
		mapDataToValue: (data: any) => data[COL_NAMES.cardBgColor],
		maxWidth: 100,
		renderCell: (params: any) => {
			const { value } = params;
			const [css] = useStyletron();
			return (
				<div
					className={css({
						backgroundColor: value,
						height: '30px',
						width: '100%'
					})}
				/>
			);
		}
	}),
	CustomColumn({
		title: 'Card Bg Image',
		minWidth: 300,
		maxWidth: 300,
		mapDataToValue: (data: any) => data[COL_NAMES.cardBgUrl],
		renderCell: ({ value }: { value: any }) => {
			const [css] = useStyletron();
			return (
				<a href={value} target='_blank' rel='noreferrer'>
					<img src={value} alt='' className={css({ height: '90px' })} />
				</a>
			);
		}
	}),
	BooleanColumn({
		title: 'Is Crossable',
		maxWidth: 200,
		mapDataToValue: (data: any) => data[COL_NAMES.isCrossable]
	}),
	CustomColumn({
		title: 'Image Or Video',
		minWidth: 200,
		maxWidth: 200,
		mapDataToValue: (data: any) => data[COL_NAMES.imageUrl] || data[COL_NAMES.videoUrl] || EMPTY_STRING,
		renderCell: ({ value }: { value: string }) => {
			if (value.endsWith('.mp4')) {
				return (
					/* eslint-disable-next-line jsx-a11y/media-has-caption */
					<video width='200' height='100' controls>
						<source src={value} type='video/mp4' />
						Your browser does not support the video tag.
					</video>
				);
			}
			const [css] = useStyletron();
			return (
				<a href={value} target='_blank' rel='noreferrer'>
					<img src={value} alt='' className={css({ height: '90px' })} />
				</a>
			);
		}
	}),
	CategoricalColumn({
		title: 'Inclusive Segments',
		maxWidth: 400,
		mapDataToValue: (data: any) => data[COL_NAMES.inclusiveSegments] || EMPTY_STRING,
		type: 'string'
	}),
	CategoricalColumn({
		title: 'Exclusive Segments',
		maxWidth: 400,
		mapDataToValue: (data: any) => data[COL_NAMES.exclusiveSegments] || EMPTY_STRING,
		type: 'string'
	}),
	CategoricalColumn({
		title: 'AB Experiments',
		maxWidth: 400,
		mapDataToValue: (data: any) => (data[COL_NAMES.abExperiments] ? JSON.stringify(data[COL_NAMES.abExperiments]) : EMPTY_STRING),
		type: 'string'
	}),
	NumericalColumn({
		title: 'Min Games Cutoff',
		maxWidth: 200,
		mapDataToValue: (data: any) => data[COL_NAMES.minGames] || 0
	}),
	NumericalColumn({
		title: 'Min App Version',
		maxWidth: 200,
		mapDataToValue: (data: any) => data[COL_NAMES.minAppVersion] || 0
	}),
	NumericalColumn({
		title: 'Max App Version',
		maxWidth: 200,
		mapDataToValue: (data: any) => data[COL_NAMES.maxAppVersion] || 0
	}),
	CategoricalColumn({
		title: 'Title English Text',
		maxWidth: 400,
		mapDataToValue: (data: any) =>
			isString(data[COL_NAMES.titleText])
				? data[COL_NAMES.titleText]
				: data[COL_NAMES.titleText]?.[APP_LANGUAGE.ENGLISH] ?? EMPTY_STRING
	}),

	CategoricalColumn({
		title: 'Title Hindi Text',
		maxWidth: 400,
		mapDataToValue: (data: any) => data[COL_NAMES.titleText]?.[APP_LANGUAGE.HINDI] ?? EMPTY_STRING
	}),

	CustomColumn({
		title: 'Title Font Color',
		mapDataToValue: (data: any) => data[COL_NAMES.titleColor],
		maxWidth: 200,
		renderCell: (params: any) => {
			const { value } = params;
			const [css] = useStyletron();
			return (
				<div
					className={css({
						backgroundColor: value,
						height: '30px',
						width: '100%'
					})}
				/>
			);
		}
	}),
	CategoricalColumn({
		title: 'Subtitle English Text',
		maxWidth: 400,
		mapDataToValue: (data: any) =>
			isString(data[COL_NAMES.subtitleText])
				? data[COL_NAMES.subtitleText]
				: data[COL_NAMES.subtitleText]?.[APP_LANGUAGE.ENGLISH] ?? EMPTY_STRING
	}),

	CategoricalColumn({
		title: 'Subtitle Hindi Text',
		maxWidth: 400,
		mapDataToValue: (data: any) => data[COL_NAMES.subtitleText]?.[APP_LANGUAGE.HINDI] ?? EMPTY_STRING
	}),
	CustomColumn({
		title: 'Subtitle Font Color',
		mapDataToValue: (data: any) => data[COL_NAMES.subtitleColor],
		maxWidth: 200,
		renderCell: (params: any) => {
			const { value } = params;
			const [css] = useStyletron();
			return (
				<div
					className={css({
						backgroundColor: value,
						height: '30px',
						width: '100%'
					})}
				/>
			);
		}
	})
];

export const activeFilter = [
	{
		columnKey: 'Archived',
		filterLabel: 'false',
		filterValue: false
	}
];
